import * as styles from "./MainInformations.css";

import { useAtomValue } from "jotai";
import { type JSX, useMemo, useRef } from "react";

import {
  DatePicker,
  DatePickerContent,
  DatePickerTrigger,
  DatePickerValue,
} from "@frontend/components/atoms/DatePicker";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { Input } from "@frontend/components/atoms/Input";
import { NumberField } from "@frontend/components/atoms/NumberField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { useField } from "@frontend/hooks/form";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { useLingui } from "@lingui/react/macro";

import { optionsAtom, projectForm } from "../stores/editor";
import { ScheduleSection } from "./ScheduleSection";
import { Section } from "./Section";

const fields = projectForm.fields;

const intervention = fields.intervention.fields;

export function ComplementaryTab(): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const options = useAtomValue(optionsAtom);

  const dateBegin = useField(intervention.dateBegin);
  const dateEnd = useField(intervention.dateEnd);
  const days = useField(intervention.interventionDays);
  const units = useField(intervention.units);
  const unitType = useField(intervention.unitTypeId);

  const unitsRef = useRef<HTMLInputElement>(null);

  const unitTypeItems = useMemo(
    () => options.unitTypes.map(({ id, label }) => ({ value: id, label })),
    [options.unitTypes],
  );
  const estimatedHours = useField(intervention.estimatedHours);
  const estimatedValuedHours = useField(intervention.estimatedValuedHours);

  return (
    <div className={styles.tab}>
      <div className={styles.top}>
        <div className={styles.column}>
          <Section
            icon="CalendarDays"
            legend={t`Dates and schedule`}
            gutter={false}
          >
            <Field>
              <FieldLabel>{t`Start date`}</FieldLabel>

              <FieldContent>
                <DatePicker
                  locale={locale}
                  value={dateBegin.value ?? null}
                  onValueChange={(next) => dateBegin.setValue(next)}
                >
                  <DatePickerTrigger>
                    <DatePickerValue placeholder={t`Enter a date`} />
                  </DatePickerTrigger>

                  <DatePickerContent />
                </DatePicker>

                <FieldError match={Boolean(dateBegin.error)}>
                  {dateBegin.error}
                </FieldError>
              </FieldContent>
            </Field>

            <Field>
              <FieldLabel>{t`Duration in days`}</FieldLabel>

              <FieldContent>
                <Input
                  type="number"
                  min={0}
                  value={null == days.value ? "" : String(days.value)}
                  onChange={(event) =>
                    days.setValue(
                      "" === event.target.value
                        ? null
                        : Number(event.target.value),
                    )
                  }
                />
              </FieldContent>
            </Field>

            <Field>
              <FieldLabel>{t`End date`}</FieldLabel>

              <FieldContent>
                <DatePicker
                  locale={locale}
                  value={dateEnd.value ?? null}
                  onValueChange={(next) => dateEnd.setValue(next)}
                >
                  <DatePickerTrigger>
                    <DatePickerValue placeholder={t`Enter a date`} />
                  </DatePickerTrigger>

                  <DatePickerContent />
                </DatePicker>

                <FieldError match={Boolean(dateEnd.error)}>
                  {dateEnd.error}
                </FieldError>
              </FieldContent>
            </Field>
          </Section>
        </div>

        <div className={styles.column}>
          <Section icon="Ruler" legend={t`Estimates`} gutter={false}>
            <Field invalid={Boolean(unitType.error)}>
              <FieldLabel>{t`Units`}</FieldLabel>

              <FieldContent>
                {/* The kind of unit rides inside the count it qualifies. */}
                <NumberField
                  inputRef={unitsRef}
                  min={0}
                  value={units.value ?? null}
                  onValueChange={(next) => units.setValue(next)}
                  rightSection={
                    <Select<number | null>
                      items={unitTypeItems}
                      value={unitType.value ?? null}
                      onValueChange={(next) => unitType.setValue(next)}
                      size="sm"
                    >
                      <SelectTrigger
                        variant="ghost"
                        aria-label={t`Unit type`}
                        className={styles.unitType}
                      >
                        <SelectValue placeholder={t`Unit`} />
                      </SelectTrigger>

                      <SelectContent
                        finalFocus={unitsRef}
                        alignItemWithTrigger={false}
                        popupWidth="content"
                      >
                        {options.unitTypes.map(({ id, label }) => (
                          <SelectItem key={id} value={id}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  }
                />

                <FieldError match={Boolean(unitType.error)}>
                  {unitType.error}
                </FieldError>
              </FieldContent>
            </Field>

            <Field>
              <FieldLabel>{t`Estimated hours`}</FieldLabel>

              <FieldContent>
                <Input
                  placeholder="0h00"
                  value={estimatedHours.value ?? ""}
                  onChange={(event) =>
                    estimatedHours.setValue(event.target.value)
                  }
                />

                <FieldError match={Boolean(estimatedHours.error)}>
                  {estimatedHours.error}
                </FieldError>
              </FieldContent>
            </Field>

            <Field>
              <FieldLabel>{t`Estimated valued hours`}</FieldLabel>

              <FieldContent>
                <Input
                  placeholder="0h00"
                  value={estimatedValuedHours.value ?? ""}
                  onChange={(event) =>
                    estimatedValuedHours.setValue(event.target.value)
                  }
                />

                <FieldError match={Boolean(estimatedValuedHours.error)}>
                  {estimatedValuedHours.error}
                </FieldError>
              </FieldContent>
            </Field>
          </Section>
        </div>
      </div>

      <ScheduleSection />
    </div>
  );
}
